import { useEffect, useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { asUserId } from '@leanin/shared/ids';
import { useMember } from '@/lib/use-session';
import { getApiClient } from '@/lib/api-client';
import { useDmReachability } from '@/lib/queries/messaging';
import { MessagesClient } from './-components/MessagesClient';

export type MessagesTab = 'all' | 'unread' | 'requests';

type MessagesSearch = {
  c?: string;
  compose?: string;
  q?: string;
  tab?: MessagesTab;
};

/**
 * Single source of truth for `/messages` URLs. All callers route through
 * here so the search-param shape (`c`, `compose`) and the deep-link hash
 * convention (`#message-<id>`) stay in lockstep with `validateSearch`
 * above — a future rename ripples through one file, not the codebase.
 *
 * Pass either an existing `conversationId` (lands on that thread) or a
 * `composeUserId` (route find-or-creates a DM with that user). Optional
 * `messageId` adds the deep-link hash so the client scrolls to a specific
 * message. Optional `tab` selects the inbox scope (`all` is the default and
 * is left out of the URL so the canonical link stays clean).
 */
export function messagesHref(opts: {
  readonly conversationId?: string;
  readonly composeUserId?: string;
  readonly messageId?: string;
  readonly tab?: MessagesTab;
}): string {
  const params = new URLSearchParams();
  if (opts.conversationId) params.set('c', opts.conversationId);
  if (opts.composeUserId) params.set('compose', opts.composeUserId);
  if (opts.tab !== undefined && opts.tab !== 'all') params.set('tab', opts.tab);
  const qs = params.toString();
  const hash = opts.messageId ? `#message-${opts.messageId}` : '';
  return `/messages${qs ? `?${qs}` : ''}${hash}`;
}

function pickStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

// `all` is the implicit default and is never written to the URL, so only the
// two non-default scopes round-trip through the search param.
function pickTab(v: unknown): Exclude<MessagesTab, 'all'> | undefined {
  return v === 'unread' || v === 'requests' ? v : undefined;
}

export const Route = createFileRoute('/_authed/messages/')({
  validateSearch: (raw: Record<string, unknown>): MessagesSearch => ({
    ...(pickStr(raw.c) ? { c: pickStr(raw.c) as string } : {}),
    ...(pickStr(raw.compose) ? { compose: pickStr(raw.compose) as string } : {}),
    ...(pickStr(raw.q) ? { q: pickStr(raw.q) as string } : {}),
    ...(pickTab(raw.tab) ? { tab: pickTab(raw.tab) } : {}),
  }),
  component: MessagesPage,
});

async function findOrCreateDirectConversation(userId: string): Promise<string | null> {
  const client = getApiClient();
  const { data, error, response } = await client.POST('/api/v1/conversations', {
    body: { kind: 'dm', userIds: [asUserId(userId)] },
  });
  if (data !== undefined) return data.data.id;
  if (response.status === 409) {
    const body = error as
      | { error?: { details?: { conversationId?: string } } }
      | undefined;
    const existing = body?.error?.details?.conversationId;
    if (existing !== undefined) return existing;
  }
  return null;
}

function MessagesPage() {
  const search = Route.useSearch();
  const member = useMember();
  const navigate = useNavigate({ from: Route.fullPath });

  const composeMutation = useMutation({
    mutationFn: (userId: string) => findOrCreateDirectConversation(userId),
    onSuccess: (conversationId) => {
      // Hand the resolved thread to the client through the URL — `c` is the
      // single source of truth for the open thread (MessagesClient mirrors
      // its selection back into it). `compose` is spent: leaving it in place
      // would re-fire the find-or-create on refresh.
      if (conversationId !== null) {
        void navigate({
          to: '/messages',
          search: (prev) => {
            const { compose: _spent, ...rest } = prev;
            return { ...rest, c: conversationId };
          },
          replace: true,
        });
      }
    },
  });

  const compose = search.compose;
  const shouldCompose =
    compose !== undefined &&
    isUuid(compose) &&
    member !== null &&
    compose !== member.id;

  // Before creating anything, ask whether a DM to this member would land as a
  // pending request. If it would, the compose modal opens pre-filled instead
  // (via `composePrefillUserId`), so the viewer sees the "message request"
  // notice and creates the conversation explicitly — a profile "Message"
  // click must never silently file a request in someone's queue. When the
  // gate is off (or the viewer is staff) the server answers `false` and the
  // fast path below creates/opens the thread as before.
  const reachabilityQuery = useDmReachability(
    shouldCompose ? asUserId(compose) : null,
  );
  const composeWouldBeRequest =
    reachabilityQuery.data?.wouldBeRequest === true;
  // On a reachability error, fall back to direct creation rather than leaving
  // the Message click dead — the server re-classifies at create time anyway.
  const reachabilityResolved =
    reachabilityQuery.data !== undefined || reachabilityQuery.isError;

  // Kick off compose-conversation creation once per target. The ref (not the
  // mutation's own state) guards the double-fire window where two effect runs
  // land before `isPending` flips. The mutation's onSuccess rewrites the URL
  // (`compose` → `c`), which is how MessagesClient learns about the thread.
  const composeStartedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!shouldCompose || compose === undefined) return;
    if (!reachabilityResolved || composeWouldBeRequest) return;
    if (composeStartedForRef.current === compose) return;
    composeStartedForRef.current = compose;
    composeMutation.mutate(compose);
  }, [
    shouldCompose,
    compose,
    reachabilityResolved,
    composeWouldBeRequest,
    composeMutation,
  ]);

  const initialId = search.c ?? null;

  return (
    <MessagesClient
      initialId={initialId}
      composePrefillUserId={
        shouldCompose && composeWouldBeRequest ? asUserId(compose) : null
      }
    />
  );
}
